<template>
  <div class="vod-question-page">
    <!-- ===== 问卷详情视图：题目管理 ===== -->
    <div v-if="detailQ" class="detail-wrap">
      <div class="detail-head">
        <a-button icon="left" style="margin-right: 12px" @click="backToList">返回问卷列表</a-button>
        <b>问卷 #{{ detailQ.id }}：{{ detailQ.title }}</b>
        <span class="cfg-hint" style="margin-left: 12px">共 {{ questions.length }} 题 · 上移/下移调整顺序</span>
        <a-button type="primary" icon="plus" style="float: right" @click="openQuestionModal()">新增题目</a-button>
      </div>
      <a-table :columns="questionColumns" :data-source="questions" :pagination="false" row-key="id" size="middle" :scroll="{ x: 900 }">
        <template slot="type" slot-scope="text">
          <a-tag :color="typeColor(text)">{{ typeLabel(text) }}</a-tag>
        </template>
        <template slot="isRequired" slot-scope="text">
          <a-tag :color="text === 1 ? 'green' : 'blue'">{{ text === 1 ? '必答' : '选答' }}</a-tag>
        </template>
        <template slot="score" slot-scope="text, record">
          {{ record.type === 'score' ? (record.scoreMin || 1) + '~' + (record.scoreMax || 5) + '分' : (record.questionScore != null ? record.questionScore + '分' : '—') }}
        </template>
        <template slot="standardAnswer" slot-scope="text, record">
          <span :title="text">{{ formatAnswer(record) }}</span>
        </template>
        <template slot="optionCount" slot-scope="text, record">
          {{ (record.options && record.options.length) ? record.options.length + ' 项' : '—' }}
        </template>
        <template slot="action" slot-scope="text, record, index">
          <a-button size="small" :disabled="index === 0" @click="moveQuestion(index, -1)">上移</a-button>
          <a-button size="small" :disabled="index === questions.length - 1" @click="moveQuestion(index, 1)">下移</a-button>
          <a-button size="small" style="margin-left: 6px" @click="openQuestionModal(record.id)">编辑</a-button>
          <a-popconfirm title="确认删除该题目？选项将一并删除" @confirm="delQuestion(record.id)">
            <a-button size="small" type="danger" style="margin-left: 6px">删除</a-button>
          </a-popconfirm>
        </template>
      </a-table>
    </div>

    <!-- ===== 问卷列表视图 ===== -->
    <div v-else>
      <div class="toolbar">
        <a-input v-model="qFilter.title" placeholder="问卷名称搜索" allow-clear style="width: 220px" @keyup.enter="loadQuestionnaires(1)" />
        <a-button type="primary" style="margin-left: 8px" @click="loadQuestionnaires(1)">搜索</a-button>
        <a-button type="primary" icon="plus" style="float: right" @click="openQuestionnaireModal()">新建问卷</a-button>
      </div>
      <a-table
        :columns="questionnaireColumns"
        :data-source="questionnaires"
        row-key="id"
        size="middle"
        :loading="qLoading"
        :scroll="{ x: 900 }"
        :pagination="{ current: qPage, pageSize: qPageSize, total: qTotal, showSizeChanger: false }"
        @change="onQTableChange"
      >
        <template slot="status" slot-scope="text">
          <a-tag :color="text === 1 ? 'green' : 'red'">{{ text === 1 ? '启用' : '禁用' }}</a-tag>
        </template>
        <template slot="config" slot-scope="text, record">
          <a-tag :color="parseConfig(record.configJson).enableScore === false ? 'orange' : 'green'">
            {{ parseConfig(record.configJson).enableScore === false ? '不计分' : '计分' }}
          </a-tag>
        </template>
        <template slot="action" slot-scope="text, record">
          <a-button size="small" type="primary" ghost @click="openDetail(record.id)">题目</a-button>
          <a-button size="small" style="margin-left: 6px" @click="openQuestionnaireModal(record.id)">编辑</a-button>
          <a-button size="small" style="margin-left: 6px" @click="toggleQuestionnaireStatus(record.id, record.status === 1 ? 0 : 1)">
            {{ record.status === 1 ? '禁用' : '启用' }}
          </a-button>
          <a-popconfirm title="确认删除该问卷？将同时解除其绑定的所有点位" @confirm="delQuestionnaire(record.id)">
            <a-button size="small" type="danger" style="margin-left: 6px">删除</a-button>
          </a-popconfirm>
        </template>
      </a-table>
    </div>

    <!-- ===== 问卷 新建/编辑 弹窗 ===== -->
    <a-modal :visible="qModal.visible" :title="qModal.id ? '编辑问卷 #' + qModal.id : '新建问卷'" @cancel="closeQModal" @ok="saveQuestionnaire" :confirm-loading="qModal.saving" width="520px">
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item label="问卷名称" required>
          <a-input v-model="qForm.title" placeholder="问卷名称" />
        </a-form-model-item>
        <a-form-model-item label="问卷说明">
          <a-textarea v-model="qForm.desc" :rows="2" placeholder="问卷说明" />
        </a-form-model-item>
        <a-form-model-item label="状态">
          <a-select v-model="qForm.status" style="width: 200px">
            <a-select-option :value="1">启用</a-select-option>
            <a-select-option :value="0">禁用</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="计分开关">
          <a-switch v-model="qForm.enableScore" checked-children="计分" un-checked-children="不计分" />
          <span class="cfg-hint" style="margin-left: 8px">关闭 = 只收集答案，不判分、无总分，统计不显示分数指标</span>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- ===== 题目 新增/编辑 弹窗 ===== -->
    <a-modal :visible="quModal.visible" :title="quModal.id ? '编辑题目 #' + quModal.id : '新增题目'" width="640px" @cancel="closeQuModal" @ok="saveQuestion">
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item label="题干" required>
          <a-input v-model="quForm.title" placeholder="题目内容" />
        </a-form-model-item>
        <a-form-model-item label="题型">
          <a-select v-model="quForm.type" style="width: 200px" @change="onQuTypeChange">
            <a-select-option value="radio">单选</a-select-option>
            <a-select-option value="checkbox">多选</a-select-option>
            <a-select-option value="input">填空</a-select-option>
            <a-select-option value="score">评分</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="是否必答">
          <a-select v-model="quForm.isRequired" style="width: 200px">
            <a-select-option :value="1">必答</a-select-option>
            <a-select-option :value="0">选答</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="每题分值">
          <a-input-number v-model="quForm.questionScore" :min="0" :precision="0" />
        </a-form-model-item>
        <a-form-model-item v-if="quForm.type === 'score'" label="评分范围">
          <a-input-number v-model="quForm.scoreMin" :min="0" style="width: 100px" /> ~
          <a-input-number v-model="quForm.scoreMax" :min="0" style="width: 100px" />
        </a-form-model-item>
        <a-form-model-item v-if="quForm.type === 'input'" label="参考答案">
          <a-input v-model="quForm.standardAnswer" placeholder="填空题的参考答案（后端按此判分）" />
        </a-form-model-item>
        <a-form-model-item v-if="quForm.type === 'radio' || quForm.type === 'checkbox'" label="选项">
          <div class="option-list">
            <div v-for="(opt, idx) in quForm.options" :key="idx" class="option-row">
              <span class="idx">{{ idx + 1 }}</span>
              <a-input v-model="opt.content" :placeholder="'选项 ' + String.fromCharCode(65 + idx)" style="flex: 1" />
              <a-checkbox v-model="opt.right">正确</a-checkbox>
              <a-button type="link" size="small" @click="removeOption(idx)">移除</a-button>
            </div>
            <a-button type="dashed" block icon="plus" @click="addOption">添加选项</a-button>
            <div class="cfg-hint" style="margin-top: 6px">在选项下方勾选「正确」即为标准答案（{{ quForm.type === 'radio' ? '单选勾 1 个' : '多选可勾多个' }}）；不勾选则该题不判分</div>
          </div>
        </a-form-model-item>
        <a-form-model-item label="答案解析">
          <a-textarea v-model="quForm.answerTip" :rows="2" placeholder="答题后的解析/提示文字" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { get, post, put, del } from './api'
import { typeLabel, typeColor, parseConfig, formatAnswer } from './util'

export default {
  name: 'VodQuestionnaire',
  data() {
    return {
      questionnaires: [],
      qLoading: false,
      qPage: 1,
      qPageSize: 10,
      qTotal: 0,
      qFilter: { title: '' },

      detailQ: null,
      questions: [],

      qModal: { visible: false, id: null, saving: false },
      qForm: { title: '', desc: '', status: 1, enableScore: true },

      quModal: { visible: false, id: null, saving: false },
      quForm: this.blankQuestionForm()
    }
  },
  computed: {
    questionnaireColumns() {
      return [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '问卷名称', dataIndex: 'title' },
        { title: '说明', dataIndex: 'desc', ellipsis: true },
        { title: '状态', dataIndex: 'status', width: 80, scopedSlots: { customRender: 'status' } },
        { title: '计分/认证', dataIndex: 'configJson', width: 150, scopedSlots: { customRender: 'config' } },
        { title: '创建时间', dataIndex: 'createdTime', width: 170 },
        { title: '操作', key: 'action', width: 240, scopedSlots: { customRender: 'action' } }
      ]
    },
    questionColumns() {
      return [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '题干', dataIndex: 'title' },
        { title: '类型', dataIndex: 'type', width: 90, scopedSlots: { customRender: 'type' } },
        { title: '必答', dataIndex: 'isRequired', width: 80, scopedSlots: { customRender: 'isRequired' } },
        { title: '分值', dataIndex: 'questionScore', width: 110, scopedSlots: { customRender: 'score' } },
        { title: '标准答案', dataIndex: 'standardAnswer', width: 200, scopedSlots: { customRender: 'standardAnswer' } },
        { title: '选项数', key: 'optionCount', width: 90, scopedSlots: { customRender: 'optionCount' } },
        { title: '操作', key: 'action', width: 250, scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  methods: {
    typeLabel,
    typeColor,
    parseConfig,
    formatAnswer,

    blankQuestionForm() {
      return {
        title: '', type: 'radio', isRequired: 1, questionScore: 1,
        scoreMin: 1, scoreMax: 5, standardAnswer: '', answerTip: '',
        options: [{ content: '', right: false }]
      }
    },

    /* ===== 问卷列表 ===== */
    loadQuestionnaires(page) {
      this.qLoading = true
      get('/vod/questionnaire/list', {
        title: this.qFilter.title || undefined,
        pageNum: page || 1,
        pageSize: this.qPageSize
      }).then(body => {
        this.questionnaires = body.data || []
        this.qTotal = body.count != null ? body.count : this.questionnaires.length
        this.qPage = page || 1
      }).catch(e => {
        this.$message.error(e.message)
      }).finally(() => {
        this.qLoading = false
      })
    },
    onQTableChange(pagination) {
      this.loadQuestionnaires(pagination.current)
    },
    openQuestionnaireModal(id) {
      this.qModal.id = id || null
      this.qForm = { title: '', desc: '', status: 1, enableScore: true }
      this.qModal.visible = true
      if (id) {
        get('/vod/questionnaire/' + id).then(body => {
          const q = body.data
          const cfg = parseConfig(q.configJson)
          this.qForm = {
            title: q.title || '',
            desc: q.desc || '',
            status: q.status == null ? 1 : q.status,
            enableScore: cfg.enableScore !== false
          }
        }).catch(e => this.$message.error(e.message))
      }
    },
    closeQModal() {
      if (!this.qModal.saving) this.qModal.visible = false
    },
    saveQuestionnaire() {
      if (!this.qForm.title || !this.qForm.title.trim()) {
        this.$message.error('问卷名称不能为空')
        return
      }
      const payload = {
        title: this.qForm.title.trim(),
        desc: (this.qForm.desc || '').trim(),
        status: this.qForm.status,
        configJson: { enableScore: !!this.qForm.enableScore }
      }
      this.qModal.saving = true
      const req = this.qModal.id ? put('/vod/questionnaire', Object.assign({ id: this.qModal.id }, payload)) : post('/vod/questionnaire', payload)
      req.then(body => {
        this.$message.success(this.qModal.id ? '问卷已更新' : '问卷已创建（ID=' + body.data + '）')
        this.qModal.visible = false
        this.loadQuestionnaires(this.qPage)
      }).catch(e => this.$message.error(e.message)).finally(() => {
        this.qModal.saving = false
      })
    },
    toggleQuestionnaireStatus(id, status) {
      put('/vod/questionnaire', { id: id, status: status }).then(() => {
        this.$message.success(status === 1 ? '问卷已启用' : '问卷已禁用')
        this.loadQuestionnaires(this.qPage)
      }).catch(e => this.$message.error(e.message))
    },
    delQuestionnaire(id) {
      del('/vod/questionnaire/' + id).then(() => {
        this.$message.success('已删除')
        this.loadQuestionnaires(this.qPage)
      }).catch(e => this.$message.error(e.message))
    },

    /* ===== 问卷详情：题目管理 ===== */
    openDetail(id) {
      get('/vod/questionnaire/' + id).then(body => {
        this.detailQ = body.data
        this.questions = body.data.questions || []
      }).catch(e => this.$message.error(e.message))
    },
    backToList() {
      this.detailQ = null
      this.questions = []
      this.loadQuestionnaires(this.qPage)
    },
    moveQuestion(idx, dir) {
      const j = idx + dir
      if (j < 0 || j >= this.questions.length) return
      const tmp = this.questions[idx]
      this.$set(this.questions, idx, this.questions[j])
      this.$set(this.questions, j, tmp)
      const ids = this.questions.map(q => q.id)
      put('/vod/questionnaire/question/sort', ids).then(() => {
        this.$message.success('顺序已更新')
      }).catch(e => this.$message.error('顺序保存失败：' + e.message))
    },

    /* ===== 题目 新增/编辑/删除 ===== */
    openQuestionModal(id) {
      this.quModal.id = id || null
      this.quForm = this.blankQuestionForm()
      this.quModal.visible = true
      if (id) {
        get('/vod/questionnaire/question/' + id).then(body => {
          const q = body.data
          const opts = (q.options && q.options.length) ? q.options : [{ content: '', right: false }]
          const rightIds = {}
          if (q.standardAnswer) {
            String(q.standardAnswer).split('、').forEach(s => { if (s.trim()) rightIds[String(s.trim())] = true })
          }
          this.quForm = {
            title: q.title || '',
            type: q.type || 'radio',
            isRequired: q.isRequired == null ? 1 : q.isRequired,
            questionScore: q.questionScore != null ? q.questionScore : 1,
            scoreMin: q.scoreMin != null ? q.scoreMin : 1,
            scoreMax: q.scoreMax != null ? q.scoreMax : 5,
            standardAnswer: q.standardAnswer || '',
            answerTip: q.answerTip || '',
            options: opts.map(o => ({ id: o.id, content: o.content, right: o.id != null && rightIds[String(o.id)] === true }))
          }
          if (!this.quForm.options.length) this.quForm.options = [{ content: '', right: false }]
        }).catch(e => this.$message.error(e.message))
      }
    },
    closeQuModal() {
      if (!this.quModal.saving) this.quModal.visible = false
    },
    onQuTypeChange() {
      this.quForm.options = [{ content: '', right: false }]
      if (this.quForm.type === 'score') {
        this.quForm.standardAnswer = ''
        this.quForm.scoreMin = 1
        this.quForm.scoreMax = 5
      }
    },
    addOption() {
      this.quForm.options.push({ content: '', right: false })
    },
    removeOption(idx) {
      this.quForm.options.splice(idx, 1)
      if (!this.quForm.options.length) this.quForm.options = [{ content: '', right: false }]
    },
    delQuestion(id) {
      del('/vod/questionnaire/question/' + id).then(() => {
        this.$message.success('已删除')
        this.openDetail(this.detailQ.id)
      }).catch(e => this.$message.error(e.message))
    },
    saveQuestion() {
      if (!this.quForm.title || !this.quForm.title.trim()) {
        this.$message.error('题干不能为空')
        return
      }
      const type = this.quForm.type
      const payload = {
        questionnaireId: this.detailQ.id,
        title: this.quForm.title.trim(),
        type: type,
        isRequired: this.quForm.isRequired,
        questionScore: this.quForm.questionScore || 0,
        scoreMin: type === 'score' ? (this.quForm.scoreMin || 1) : null,
        scoreMax: type === 'score' ? (this.quForm.scoreMax || 5) : null,
        standardAnswer: type === 'score' ? '' : (this.quForm.standardAnswer || '').trim(),
        answerTip: (this.quForm.answerTip || '').trim()
      }
      if (type === 'radio' || type === 'checkbox') {
        const options = []
        const correctIdx = []
        this.quForm.options.forEach((o, i) => {
          const content = (o.content || '').trim()
          if (content) {
            options.push({ id: o.id || null, content: content })
          }
          if (o.right) correctIdx.push(i)
        })
        if (!options.length) {
          this.$message.error('单选/多选至少需要一个选项')
          return
        }
        payload.options = options
        if (correctIdx.length) {
          payload.correctIndexes = correctIdx.join('、')
        }
      }
      this.quModal.saving = true
      const req = this.quModal.id ? put('/vod/questionnaire/question', Object.assign({ id: this.quModal.id }, payload)) : post('/vod/questionnaire/question', payload)
      req.then(() => {
        this.$message.success(this.quModal.id ? '题目已更新' : '题目已创建')
        this.quModal.visible = false
        this.openDetail(this.detailQ.id)
      }).catch(e => this.$message.error(e.message)).finally(() => {
        this.quModal.saving = false
      })
    }
  },
  mounted() {
    this.loadQuestionnaires(1)
  }
}
</script>

<style scoped>
.vod-question-page { padding: 16px; }
.cfg-hint { font-size: 12px; color: #8b9aab; }
.toolbar { margin-bottom: 12px; }
.detail-wrap {
  border: 1px solid #e8e8e8; border-radius: 4px; padding: 12px; background: #fff;
}
.detail-head { margin-bottom: 12px; line-height: 32px; }
.option-list { width: 100%; }
.option-row {
  display: flex; align-items: center; margin-bottom: 6px;
}
.option-row .idx {
  width: 24px; text-align: center; color: #999; flex: none;
}
.option-row .ant-checkbox-wrapper { margin: 0 10px; flex: none; }
</style>
